"use client";

/**
 * Zustandsquelle für das Statusband (A6).
 * Primär SSE (Probe-/Durchlauf), Fallback Poll `/api/dev/testlauf/aktiv` alle 5 s.
 * Verbindungsabbruch ≠ Testergebnis.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AktiverTestlauf } from "@/dev-testbed/testlauf/typen.ts";
import {
  readTestmodusSession,
  type TestmodusSession,
} from "@/dev-testbed/testmodus/session.ts";
import { fehlertextFuerBand } from "./fehlertext.ts";
import {
  bauSchrittkette,
  formatDauerSekunden,
  formatKostenUsd,
  handlungsaufforderungAusRezept,
  TESTZUSTAND_TEXT,
  type StatusbandSchrittPunkt,
  type Testzustand,
} from "./status-modell.ts";

const POLL_MS = 2_000;
const HINT_COOKIE = "dev_scope_aktiv";

export type TestlaufStatusView = {
  sichtbar: boolean;
  zustand: Testzustand;
  /** Klartext in der Mitte (inkl. Zähler / Fehlergrund). */
  zustandText: string;
  verbindungUnterbrochen: boolean;
  elapsedSeconds: number;
  prozessTitel: string;
  schrittTitel: string;
  schrittNummer: number;
  schrittGesamt: number;
  unternehmenName: string;
  dauerMs: number | null;
  costUsd: number | null;
  fehlerTechnisch: string | null;
  schritte: StatusbandSchrittPunkt[];
  /** Ob Rezept `manuell` in fertigWenn hat (Fertig-melden-Knopf). */
  manuellFertig: boolean;
  /** Scope oder Legacy-Session vorhanden. */
  hatScope: boolean;
  hatSession: boolean;
  session: TestmodusSession | null;
  aktiv: AktiverTestlauf | null;
};

type SseDone = {
  status: string;
  summary: string;
  costUsd?: number;
  durationMs?: number;
};

type BekannterSchritt = { nr: number; code: string; titel: string };

function hatHinweisCookie(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie
    .split(";")
    .map((p) => p.trim())
    .some((p) => p === `${HINT_COOKIE}=1`);
}

function startedAtAusAktiv(aktiv: AktiverTestlauf): number | null {
  const expires = Date.parse(aktiv.expiresAt);
  if (!Number.isFinite(expires)) return null;
  const warte = aktiv.wartezeitSekunden;
  if (typeof warte !== "number" || !Number.isFinite(warte)) return null;
  return expires - warte * 1000;
}

function startedAtAusSession(session: TestmodusSession | null): number | null {
  if (!session?.gestartetAm) return null;
  const t = Date.parse(session.gestartetAm);
  return Number.isFinite(t) ? t : null;
}

async function ladeAktiv(): Promise<
  | { art: "aktiv"; lauf: AktiverTestlauf }
  | { art: "abschluss"; signal: AbschlussPoll }
  | { art: "leer" }
> {
  if (!hatHinweisCookie()) {
    return { art: "leer" };
  }
  try {
    const res = await fetch("/api/dev/testlauf/aktiv", {
      method: "GET",
      credentials: "same-origin",
      cache: "no-store",
    });
    if (!res.ok) return { art: "leer" };
    const data: unknown = await res.json();
    if (data === null) return { art: "leer" };
    if (!data || typeof data !== "object") return { art: "leer" };
    const o = data as Record<string, unknown>;
    if (o.abschluss === "erfolgreich" || o.abschluss === "fehlgeschlagen") {
      return {
        art: "abschluss",
        signal: {
          abschluss: o.abschluss,
          fehlergrund: typeof o.fehlergrund === "string" ? o.fehlergrund : null,
          dauerMs: typeof o.dauerMs === "number" ? o.dauerMs : null,
          costUsd: typeof o.costUsd === "number" ? o.costUsd : null,
        },
      };
    }
    return { art: "aktiv", lauf: data as AktiverTestlauf };
  } catch {
    return { art: "leer" };
  }
}

type AbschlussPoll = {
  abschluss: "erfolgreich" | "fehlgeschlagen";
  fehlergrund: string | null;
  dauerMs: number | null;
  costUsd: number | null;
};

async function ladeProzessSchritte(
  prozessCode: string,
): Promise<BekannterSchritt[]> {
  try {
    const res = await fetch(
      `/api/dev/prozesse/${encodeURIComponent(prozessCode)}`,
      { credentials: "same-origin", cache: "no-store" },
    );
    if (!res.ok) return [];
    const data = (await res.json()) as {
      schritte?: Array<{ code: string; nr: number; klarname: string }>;
    };
    return (data.schritte ?? []).map((s) => ({
      nr: s.nr,
      code: s.code,
      titel: s.klarname,
    }));
  } catch {
    return [];
  }
}

function mappeZustand(args: {
  aktiv: AktiverTestlauf | null;
  session: TestmodusSession | null;
  sseRunning: boolean;
  sseDone: SseDone | null;
  scopeAbschluss: AbschlussPoll | null;
}): Testzustand {
  const { aktiv, session, sseRunning, sseDone, scopeAbschluss } = args;

  if (scopeAbschluss) {
    return scopeAbschluss.abschluss === "erfolgreich"
      ? "erfolgreich"
      : "fehlgeschlagen";
  }

  if (sseDone) {
    const s = sseDone.status.toLowerCase();
    if (s === "passed" || s === "bestanden") return "erfolgreich";
    if (
      s === "failed" ||
      s === "cancelled" ||
      s === "fehlgeschlagen" ||
      s === "abgebrochen" ||
      s === "offen_abgebrochen" ||
      s === "degraded" ||
      s === "not_implemented" ||
      s === "skipped"
    ) {
      return "fehlgeschlagen";
    }
  }

  if (session?.awaitingJudgment) return "erfolgreich";

  if (session?.zustand === "pausiert") {
    return "bereit";
  }

  // Echte Ansicht: Mensch ist dran — nur solange die UI noch aktiv ist.
  const inEchterUi =
    session?.zustand !== "verlassen" &&
    (Boolean(aktiv?.einstieg?.route) || Boolean(session?.resolvedRoute));
  if (inEchterUi) {
    return "wartet_auf_dich";
  }

  if (sseRunning) return "laeuft";

  // UI verlassen, Lauf noch nicht abgeschlossen — weiter live anzeigen
  // (Beurteilen-Rücksprung darf nicht auf „bereit“ / „Test ausstehend“ fallen).
  if (
    session?.zustand === "verlassen" &&
    !session.awaitingJudgment &&
    !sseDone &&
    !scopeAbschluss
  ) {
    if (aktiv) return "laeuft";
    if (session.probeRunId) return "laeuft";
    if (session.resolvedRoute) return "wartet_auf_dich";
    return "laeuft";
  }

  if (aktiv || session) {
    if (aktiv && !sseDone && !scopeAbschluss) return "laeuft";
    if (session?.probeRunId && !sseDone) return "laeuft";
    if (session?.zustand === "laeuft") return "laeuft";
    return "bereit";
  }

  return "bereit";
}

function bauZustandText(args: {
  zustand: Testzustand;
  elapsedSeconds: number;
  handlung: string;
  dauerMs: number | null;
  costUsd: number | null;
  fehlerAnzeige: string | null;
}): string {
  switch (args.zustand) {
    case "bereit":
      return TESTZUSTAND_TEXT.bereit;
    case "laeuft":
      return TESTZUSTAND_TEXT.laeuft;
    case "wartet_auf_dich":
      return args.handlung;
    case "erfolgreich": {
      const teile: string[] = ["Erfolgreich"];
      if (args.dauerMs != null) teile.push(formatDauerSekunden(args.dauerMs));
      if (args.costUsd != null) teile.push(formatKostenUsd(args.costUsd));
      return teile.length > 1 ? `${teile[0]} · ${teile.slice(1).join(" · ")}` : teile[0]!;
    }
    case "fehlgeschlagen":
      return args.fehlerAnzeige ?? "Fehlgeschlagen";
  }
}

export function useTestlaufStatus(): TestlaufStatusView {
  const [aktiv, setAktiv] = useState<AktiverTestlauf | null>(null);
  const [session, setSession] = useState<TestmodusSession | null>(null);
  const [sseRunning, setSseRunning] = useState(false);
  const [sseDone, setSseDone] = useState<SseDone | null>(null);
  const [scopeAbschluss, setScopeAbschluss] = useState<AbschlussPoll | null>(null);
  const [verbindungUnterbrochen, setVerbindungUnterbrochen] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [bekannteSchritte, setBekannteSchritte] = useState<BekannterSchritt[]>(
    [],
  );

  const esRef = useRef<EventSource | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const doneRef = useRef<SseDone | null>(null);

  const refreshSession = useCallback(() => {
    setSession(readTestmodusSession());
  }, []);

  const refreshAktiv = useCallback(async () => {
    const next = await ladeAktiv();
    if (next.art === "aktiv") {
      setAktiv(next.lauf);
      setScopeAbschluss(null);
      return;
    }
    if (next.art === "abschluss") {
      setAktiv(null);
      setScopeAbschluss(next.signal);
      return;
    }
    setAktiv(null);
  }, []);

  useEffect(() => {
    refreshSession();
    void refreshAktiv();
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === "devTestbed.testmodus") refreshSession();
    };
    const onAbschluss = (e: Event) => {
      const detail = (e as CustomEvent<AbschlussPoll>).detail;
      if (!detail) return;
      setAktiv(null);
      setScopeAbschluss(detail);
    };
    window.addEventListener("dev-testbed-testmodus", refreshSession);
    window.addEventListener("storage", onStorage);
    window.addEventListener("dev-testbed-abschluss", onAbschluss);
    return () => {
      window.removeEventListener("dev-testbed-testmodus", refreshSession);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("dev-testbed-abschluss", onAbschluss);
    };
  }, [refreshAktiv, refreshSession]);

  // Poll nur wenn kein SSE offen ist (oder als Fallback parallel bei Scope).
  const sseOpen = sseRunning;
  useEffect(() => {
    if (sseOpen) return;
    const sichtbar =
      Boolean(aktiv) ||
      Boolean(session) ||
      Boolean(scopeAbschluss) ||
      hatHinweisCookie();
    if (!sichtbar) return;
    const id = window.setInterval(() => {
      void refreshAktiv();
      refreshSession();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [aktiv, refreshAktiv, refreshSession, scopeAbschluss, session, sseOpen]);

  // SSE: Probe-Run hat Vorrang, sonst Durchlauf-Stream liefert ui_required (kein Zustands-done).
  const probeRunId = session?.probeRunId ?? null;

  useEffect(() => {
    esRef.current?.close();
    esRef.current = null;
    setSseRunning(false);
    setVerbindungUnterbrochen(false);

    if (!probeRunId) {
      return;
    }

    // Neuer Lauf: altes done verwerfen.
    doneRef.current = null;
    setSseDone(null);
    setSseRunning(true);

    const es = new EventSource(
      `/api/dev/runs/${encodeURIComponent(probeRunId)}/stream`,
    );
    esRef.current = es;

    es.onmessage = (msg) => {
      try {
        const ev = JSON.parse(msg.data) as {
          kind: string;
          status?: string;
          summary?: string;
          costUsd?: number;
          durationMs?: number;
        };
        setVerbindungUnterbrochen(false);
        if (ev.kind === "step" || ev.kind === "log") {
          setSseRunning(true);
        }
        if (ev.kind === "done") {
          const done: SseDone = {
            status: ev.status ?? "failed",
            summary: ev.summary ?? "",
            costUsd: ev.costUsd,
            durationMs: ev.durationMs,
          };
          doneRef.current = done;
          setSseDone(done);
          setSseRunning(false);
          es.close();
          esRef.current = null;
        }
      } catch {
        /* ignore */
      }
    };

    es.onerror = () => {
      // Nicht auf fehlgeschlagen springen — Zustand behalten.
      if (!doneRef.current) {
        setVerbindungUnterbrochen(true);
        setSseRunning(false);
      }
    };

    return () => {
      es.close();
      if (esRef.current === es) esRef.current = null;
    };
  }, [probeRunId]);

  // startedAt für Sekundenzähler
  useEffect(() => {
    const fromAktiv = aktiv ? startedAtAusAktiv(aktiv) : null;
    const fromSession = startedAtAusSession(session);
    const next = fromSession ?? fromAktiv ?? (aktiv || session ? Date.now() : null);
    if (next != null) {
      startedAtRef.current = next;
    }
  }, [aktiv, session]);

  useEffect(() => {
    const sichtbar = Boolean(aktiv) || Boolean(session) || Boolean(scopeAbschluss);
    if (!sichtbar) {
      setElapsedSeconds(0);
      return;
    }
    const tick = () => {
      const start = startedAtRef.current;
      if (start == null) {
        setElapsedSeconds(0);
        return;
      }
      setElapsedSeconds(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [aktiv, scopeAbschluss, session]);

  // Schrittkette: Titel/Codes wenn Admin-Prozessdetail erreichbar.
  const prozessCode = session?.prozessCode ?? null;
  useEffect(() => {
    if (!prozessCode) {
      setBekannteSchritte([]);
      return;
    }
    let cancelled = false;
    void ladeProzessSchritte(prozessCode).then((list) => {
      if (!cancelled) setBekannteSchritte(list);
    });
    return () => {
      cancelled = true;
    };
  }, [prozessCode]);

  return useMemo((): TestlaufStatusView => {
    const hatScope = aktiv !== null;
    const hatSession = session !== null;
    const sichtbar = hatScope || hatSession || scopeAbschluss !== null;

    const prozessTitel =
      aktiv?.prozessTitel ?? session?.prozessTitel ?? "";
    const schrittTitel =
      aktiv?.schrittTitel ?? session?.schrittKlarname ?? "";
    const schrittNummer =
      aktiv?.schrittNummer ?? session?.schrittNr ?? 1;
    const schrittGesamt = aktiv?.schrittGesamt ?? Math.max(schrittNummer, 1);
    const unternehmenName =
      aktiv?.unternehmen.name ?? session?.unternehmenName ?? "";

    const handlung = handlungsaufforderungAusRezept({
      fertigWenn: aktiv?.fertigWenn ?? [],
      erlaubteAktionen: aktiv?.erlaubteAktionen ?? [],
      schrittTitel: schrittTitel || "den Schritt",
    });

    const zustand = sichtbar
      ? mappeZustand({
          aktiv,
          session,
          sseRunning,
          sseDone,
          scopeAbschluss,
        })
      : "bereit";

    const fehler =
      zustand === "fehlgeschlagen"
        ? scopeAbschluss?.fehlergrund
          ? {
              anzeige: scopeAbschluss.fehlergrund,
              technisch: scopeAbschluss.fehlergrund,
            }
          : sseDone
            ? fehlertextFuerBand(sseDone.summary, sseDone.status)
            : null
        : null;

    const dauerMs =
      scopeAbschluss?.dauerMs ??
      sseDone?.durationMs ??
      (zustand === "erfolgreich" && startedAtRef.current != null
        ? Date.now() - startedAtRef.current
        : null);
    const costUsd = scopeAbschluss?.costUsd ?? sseDone?.costUsd ?? null;

    const zustandText = bauZustandText({
      zustand,
      elapsedSeconds,
      handlung,
      dauerMs,
      costUsd,
      fehlerAnzeige: fehler?.anzeige ?? null,
    });

    const schritte = bauSchrittkette({
      schrittNummer,
      schrittGesamt,
      schrittTitel,
      bekannte: bekannteSchritte,
    });

    const manuellFertig = (aktiv?.fertigWenn ?? []).some(
      (f) => f.typ === "manuell",
    );

    return {
      sichtbar,
      zustand,
      zustandText,
      verbindungUnterbrochen,
      elapsedSeconds,
      prozessTitel,
      schrittTitel,
      schrittNummer,
      schrittGesamt,
      unternehmenName,
      dauerMs,
      costUsd,
      fehlerTechnisch: fehler?.technisch ?? null,
      schritte,
      manuellFertig,
      hatScope,
      hatSession,
      session,
      aktiv,
    };
  }, [
    aktiv,
    bekannteSchritte,
    elapsedSeconds,
    scopeAbschluss,
    session,
    sseDone,
    sseRunning,
    verbindungUnterbrochen,
  ]);
}
